/**
 * Debt Calculator for SAFECARE Act
 * Projects medical debt redemption and medical education debt reduction
 */

/**
 * Calculate medical debt redemption path (Title VII)
 */
export function calculateMedicalDebtRedemption(year, baselineData, config, index) {
    const baselineDebt = baselineData.medicalDebt.totalStock;

    // Only start redemption active years
    if (year < config.startYear) {
        return {
            year,
            baselineDebt,
            annualBudget: 0,
            debtCancelledThisYear: 0,
            totalCancelled: 0,
            remainingDebt: baselineDebt,
            reductionPercent: 0
        };
    }

    // Annual redemption budget
    const annualBudget = config.debtRedemptionBudget * config.enforceDebtRedemption;

    // Assume debts purchased at ~23 cents on the dollar
    // A $5B annual budget buys ~$22B of debt (5 / 0.227)
    // $22B * 10 years = $220B (the full debt stock)
    const purchaseDiscount = 0.227;
    const debtCancelledPerYear = annualBudget / purchaseDiscount;

    // Calculate years active (1-based)
    const yearsActive = year - config.startYear + 1;

    // Calculate remaining debt
    const totalCancelled = debtCancelledPerYear * yearsActive;
    const remainingDebt = Math.max(0, baselineDebt - totalCancelled);

    // Percent reduction
    const reductionPercent = ((baselineDebt - remainingDebt) / baselineDebt) * 100;

    return {
        year,
        baselineDebt,
        annualBudget,
        debtCancelledThisYear: Math.min(debtCancelledPerYear, remainingDebt),
        totalCancelled,
        remainingDebt,
        reductionPercent
    };
}

/**
 * Calculate medical education debt path (Section 501)
 */
export function calculateMedSchoolDebtPath(year, baselineData, config) {
    const baselineYear = baselineData.medSchoolDebt.find(d => d.year === year);
    if (!baselineYear) return null;

    const baselineDebt = baselineYear.avgDebt;

    // Tuition caps reduce debt accumulation
    // Calculate 4-year cost based on cap
    const tuitionCapTarget = (config.tuitionCapAnnual || 15000) * 4;

    // Ensure we don't accidentally increase debt if cap > baseline
    const targetDebt = Math.min(baselineDebt, tuitionCapTarget);

    // reduction = (gap between baseline and target) * percent_enforced
    const potentialSavings = baselineDebt - targetDebt;
    const actualSavings = potentialSavings * config.enforceTuitionCaps;

    const safecareDebt = baselineDebt - actualSavings;
    const savings = baselineDebt - safecareDebt;

    return {
        year,
        baselineDebt,
        safecareDebt,
        savings,
        savingsPercent: (savings / baselineDebt) * 100,
        newGraduates: baselineYear.newGraduates
    };
}

/**
 * Calculate debt projections for all years
 */
export function calculateDebtProjections(baselineData, config) {
    const medicalDebtPath = [];
    const medSchoolDebtPath = [];

    baselineData.nheBaseline.forEach((yearData, index) => {
        // Medical debt redemption
        medicalDebtPath.push(
            calculateMedicalDebtRedemption(yearData.year, baselineData, config, index)
        );

        // Medical school debt
        const medSchoolResult = calculateMedSchoolDebtPath(yearData.year, baselineData, config);
        if (medSchoolResult) {
            medSchoolDebtPath.push(medSchoolResult);
        }
    });

    return {
        medicalDebt: medicalDebtPath,
        medSchoolDebt: medSchoolDebtPath
    };
}

/**
 * Calculate aggregate impact on medical professionals
 */
export function calculateWorkforceDebtImpact(debtProjections) {
    const finalYear = debtProjections.medSchoolDebt[debtProjections.medSchoolDebt.length - 1];
    const firstYear = debtProjections.medSchoolDebt[0];

    // Total graduates over period
    const totalGraduates = debtProjections.medSchoolDebt.reduce(
        (sum, year) => sum + year.newGraduates, 0
    );

    // Average savings per graduate
    const avgSavingsPerGrad = debtProjections.medSchoolDebt.reduce(
        (sum, year) => sum + (year.savings * year.newGraduates), 0
    ) / totalGraduates;

    return {
        totalGraduates,
        avgSavingsPerGrad,
        firstYearDebt: firstYear.baselineDebt,
        finalYearDebt: finalYear.safecareDebt,
        totalReduction: firstYear.baselineDebt - finalYear.safecareDebt
    };
}
